import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { SnackbarProvider } from 'notistack';
import { RouterProvider } from 'react-router-dom';
import { Suspense, useEffect } from 'react';
import { AppThemeProvider } from '@/theme/ThemeProvider';
import { AppErrorBoundary } from '@/components/common/AppErrorBoundary';
import { PageLoader } from '@/components/common/Loaders';
import { forgetSessionState, queryClient } from './queryClient';
import { router } from '@/routes';
import { useAuthStore } from '@/store/auth';
import { setOrganizationNotVerifiedHandler, setSessionLostHandler } from '@/api/client';
import { paths } from '@/routes/paths';

export function App() {
  useEffect(() => {
    // The boot splash in `index.html` covers the page until React has
    // mounted; from here on the app draws its own loading states.
    document.getElementById('boot-splash')?.remove();
  }, []);

  useEffect(() => {
    // A refresh that fails means the session is gone for good; drop the cache so
    // the next user does not see the previous one's rows.
    setSessionLostHandler(() => {
      forgetSessionState();
      // `isPlatform` too: it is the one claim that decides whether a module is
      // drawn at all, and leaving it true would outlive the session it came from.
      useAuthStore.setState({
        phase: 'anonymous',
        user: null,
        permissions: new Set(),
        roleCode: null,
        isPlatform: false,
      });
      if (!window.location.pathname.startsWith(paths.login)) router.navigate(paths.login);
    });
    /*
     * A not-verified organization is a destination, not a failure. Any request
     * that hits it re-reads the gate and sends the caller to the wizard, so no
     * screen has to interpret the 403 for itself.
     */
    setOrganizationNotVerifiedHandler(() => {
      void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
      if (window.location.pathname !== paths.onboarding) router.navigate(paths.onboarding);
    });

    void useAuthStore.getState().bootstrap();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AppThemeProvider>
        <SnackbarProvider
          maxSnack={3}
          autoHideDuration={4000}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          // A toast appears and leaves in place: no slide, no collapse.
          transitionDuration={{ enter: 0, exit: 0 }}
        >
          <AppErrorBoundary>
            {/* Routes are lazy, so the boundary has to sit above the router —
                otherwise the first navigation suspends with nothing to catch it. */}
            <Suspense fallback={<PageLoader fullScreen />}>
              <RouterProvider router={router} />
            </Suspense>
          </AppErrorBoundary>
        </SnackbarProvider>
      </AppThemeProvider>
      {import.meta.env.DEV ? <ReactQueryDevtools initialIsOpen={false} /> : null}
    </QueryClientProvider>
  );
}
